import clsx from "clsx";
import {
  WarehouseElementData,
  WarehouseElementLocation,
} from "components/WarehouseElements/WarehouseElementsData";
import { EMPLACEMENT_TYPE, PARK_TYPE, PORTE_TYPE } from "constants/globals";
import { useAppDispatch, useAppSelector } from "hooks/StoreHooks";
import React, { JSX } from "react";
import { setOpenedDetailId } from "store/Slices/gridEventSlice";
import { createAdjacentBorders } from "utils/createAdjacentBorders";
import { GetGridCoordonates } from "utils/GetGridCoordonates";
import { DetailsWindow } from "../DetailsWindow/DetailsWindow";
import Etiquette from "../Etiquette/Etiquette";

import { selectGetElementStatus } from "store/Selectors/StatusSelectors/contentSelector";
import { StatusData } from "store/Slices/Status/statusSlice";
import "./GridElementStyle.scss";

type GridElementProps = Omit<WarehouseElementData, "deserialize">;

const GridElement: React.FC<GridElementProps> = ({
  type,
  id,
  gridElementLocationSpan,
  etiquette,
}) => {
  const isCurrentDetailsWindowOpen = useAppSelector(
    state => state.gridEventSlice.openedDetailId === id
  );

  const dispatch = useAppDispatch();

  const gridElementType = type.toLocaleLowerCase() ?? "unknown";
  const elementStatus = useAppSelector(selectGetElementStatus(id));
 
  const className: string = createGridElementClassName(type, elementStatus);
  const gridElementLocation = GetGridCoordonates(
    gridElementLocationSpan,
    etiquette.etiquetteLocation
  );

  const cellsToRender: JSX.Element[] = createAdjacentBorders(
    gridElementLocationSpan,
    id,
    gridElementType,
    gridElementLocation
  );

  const handleCellClick = (event: React.MouseEvent) => {
    event.stopPropagation();
    const nextId = [PARK_TYPE, PORTE_TYPE, EMPLACEMENT_TYPE].includes(
      gridElementType
    )
      ? isCurrentDetailsWindowOpen
        ? null
        : id
      : null;
    dispatch(setOpenedDetailId(nextId));
  };

  return (
    <>
      {isCurrentDetailsWindowOpen && (
        <DetailsWindow id={id} location={etiquette.etiquetteLocation} />
      )}
      <div
        id={"element_" + id}
        className={className}
        style={{
          display: "grid",
          gridColumnStart: getGridColumnStart(
            gridElementLocation,
            gridElementType
          ),
          gridColumnEnd: getGridColumnEnd(gridElementLocation, gridElementType),
          gridRowStart: getGridRowStart(gridElementLocation, gridElementType),
          gridRowEnd: getGridRowEnd(gridElementLocation, gridElementType),
        }}>
        {cellsToRender}
        {etiquette && (
          <Etiquette
            key={"Etiquette" + id}
            location={etiquette.etiquetteLocation}
            description={etiquette.description}
            id={id}
            type={gridElementType}
            mainGridLocation={gridElementLocation}
            clickHandler={handleCellClick}
            detailsOpened={isCurrentDetailsWindowOpen}
          />
        )}
      </div>
    </>
  );
};

export default GridElement;

function createGridElementClassName (
  type: string,
  elementStatus: StatusData
  ) : string {
  return clsx("grid-element", type.toLowerCase(),
    typeof elementStatus.status === "string" && elementStatus.status !== "INVALIDE" && elementStatus.status,
    elementStatus.status === "RSV" && elementStatus.reservationType && `rsvt-${elementStatus.reservationType}`,
    elementStatus.isOperationInProgress && "operation-in-progress",
    elementStatus.isOccupied && "OCC",
    elementStatus.hasInventory && "inventory",
    elementStatus.hasPallet && "pallet",
    elementStatus.hasCage && "cage",
    elementStatus.hasStacks && "stacks",
    elementStatus.aConfirmer && "aconfirmer"
  );
}

function getGridColumnStart(
  gridElementLocation: WarehouseElementLocation,
  gridElementType: string
): string {
  return (
    gridElementType === PARK_TYPE && gridElementLocation.start.x > 1
      ? gridElementLocation.start.x - 1
      : gridElementLocation.start.x
  ).toString();
}

function getGridColumnEnd(
  gridElementLocation: WarehouseElementLocation,
  gridElementType: string
): string {
  const isPark = gridElementType === PARK_TYPE;
  let offset = 1;
  if (isPark) {
    if (gridElementLocation.start.x <= 1) {
      return "";
    }
    offset++;
  } else {
    offset--;
  }
  if (gridElementLocation.end) {
    offset++;
  }
  return `${
    "span " +
    ((gridElementLocation.end
      ? +Math.abs(gridElementLocation.end.x - gridElementLocation.start.x)
      : gridElementLocation.start.x) +
      offset)
  }`;
}

function getGridRowStart(
  gridElementLocation: WarehouseElementLocation,
  gridElementType: string
): string {
  return (
    gridElementType === PARK_TYPE && gridElementLocation.start.y > 1
      ? gridElementLocation.start.y - 1
      : gridElementLocation.start.y
  ).toString();
}

function getGridRowEnd(
  gridElementLocation: WarehouseElementLocation,
  gridElementType: string
): string {
  let offset = 1;
  if (gridElementType === PARK_TYPE) {
    offset++;
  } else {
    offset--;
  }
  if (gridElementLocation.end) {
    offset++;
  }
  return `${
    "span " +
    ((gridElementLocation.end
      ? +Math.abs(gridElementLocation.end.y - gridElementLocation.start.y)
      : gridElementLocation.start.y) +
      offset)
  }`;
}

